/**
 * Inner Voice Vibe — Coaching Privacy Dashboard
 *
 * Lets the user see what the AI knows about them, export, or delete.
 * State-based navigation version.
 *
 * Australian English throughout.
 */

import React, { useEffect, useState } from 'react';
import { ArrowLeft, Download, Trash2, Shield, AlertTriangle } from 'lucide-react';
import { useApp } from '../context/AppContext';
import coachingService from '../services/CoachingService';
import { downloadFile, downloadReadableTranscript } from '@ddd/coaching-export';

const COUNTRIES = [
  { code: 'AU', label: 'Australia' },
  { code: 'NZ', label: 'New Zealand' },
  { code: 'IN', label: 'India' },
  { code: 'UK', label: 'United Kingdom' },
  { code: 'IE', label: 'Ireland' },
  { code: 'CA', label: 'Canada' },
  { code: 'US', label: 'United States' }
];

const COACH_NAME = 'Echo';

export default function CoachingPrivacyPage() {
  const { setCurrentView } = useApp();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    (async () => {
      const res = await coachingService.getProfile();
      if (res?.success) setProfile(res.profile);
      setLoading(false);
    })();
  }, []);

  const updatePersonalContext = async (patch) => {
    setSaving(true);
    const res = await coachingService.updateProfile({
      personalContext: { ...(profile?.personalContext || {}), ...patch }
    });
    if (res?.success) {
      setProfile(res.profile);
      setMessage({ kind: 'success', text: 'Saved.' });
    } else {
      setMessage({ kind: 'error', text: res?.error || 'Save failed.' });
    }
    setSaving(false);
    setTimeout(() => setMessage(null), 2500);
  };

  // The readable transcript leads: what was actually said, as Markdown. Raw
  // Firestore JSON stays behind a quiet secondary link for portability.
  const exportData = async (raw = false) => {
    const res = await coachingService.exportData();
    if (!res?.success) {
      setMessage({ kind: 'error', text: 'Export failed.' });
      return;
    }
    if (raw) {
      downloadFile(
        JSON.stringify(res, null, 2),
        `vibe-coaching-${new Date().toISOString().slice(0, 10)}.json`,
        'application/json'
      );
    } else {
      downloadReadableTranscript(res, {
        coachName: COACH_NAME,
        appId: 'inner-voice-vibe',
        appName: 'Inner Voice Vibe'
      });
    }
    setMessage({ kind: 'success', text: 'Downloaded.' });
    setTimeout(() => setMessage(null), 2500);
  };

  const deleteAll = async () => {
    const res = await coachingService.deleteAll();
    if (res?.success) {
      setProfile(null);
      setDeleted(true);
      setConfirmDelete(false);
    } else {
      setMessage({ kind: 'error', text: 'Delete failed — please try again.' });
    }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-slate-500">Loading…</div>;
  }

  if (deleted) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Coaching data deleted</h1>
        <p className="text-slate-700">
          All your coaching data has been permanently removed. You can start fresh anytime from{' '}
          <button onClick={() => setCurrentView('coaching')} className="text-blue-700 underline">Coach</button>.
        </p>
      </div>
    );
  }

  if (!profile?.consent?.granted) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">No coaching data yet</h1>
        <p className="text-slate-700">
          You haven't started coaching yet, so there's nothing stored. Head to{' '}
          <button onClick={() => setCurrentView('coaching')} className="text-blue-700 underline">Coach</button> when you're ready.
        </p>
      </div>
    );
  }

  const pc = profile.personalContext || {};
  const apps = profile.apps || {};

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <button
        onClick={() => setCurrentView('coaching')}
        className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-4"
      >
        <ArrowLeft size={16} /> Back to coaching
      </button>

      <div className="flex items-center gap-3 mb-6">
        <Shield className="text-blue-600" size={28} />
        <h1 className="text-2xl font-bold text-slate-900">Your coaching data</h1>
      </div>

      <p className="text-slate-700 mb-6 leading-relaxed">
        This is everything {COACH_NAME} knows about you. You can change what's stored, download your conversations,
        or delete it all. It is stored in Australia; each message is sent to Anthropic&apos;s Claude to write the
        reply and is not used to train AI. It is never sold or shared with advertisers.
      </p>

      {message && (
        <div className={`mb-4 px-4 py-2 rounded-lg text-sm ${
          message.kind === 'success' ? 'bg-green-50 text-green-900' : 'bg-red-50 text-red-900'
        }`}>{message.text}</div>
      )}

      {/* Personal context */}
      <section className="bg-white rounded-xl shadow-sm p-5 mb-5">
        <h2 className="font-semibold text-slate-900 mb-3">Your profile</h2>
        <div className="space-y-3">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Name</span>
            <input
              type="text"
              defaultValue={pc.firstName || ''}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v !== (pc.firstName || '')) updatePersonalContext({ firstName: v });
              }}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-base"
              maxLength={40}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Country (for support resources)</span>
            <select
              defaultValue={pc.country || ''}
              onChange={(e) => updatePersonalContext({ country: e.target.value || null })}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-base"
            >
              <option value="">Not set</option>
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </label>
        </div>
      </section>

      {/* Per-app state */}
      {Object.entries(apps).map(([appId, app]) => (
        <section key={appId} className="bg-white rounded-xl shadow-sm p-5 mb-5">
          <h2 className="font-semibold text-slate-900 mb-3">What {COACH_NAME} remembers</h2>

          {app.activeGoals?.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-slate-700 mb-1">Active goals</h3>
              <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
                {app.activeGoals.map((g, i) => <li key={i}>{g.description}</li>)}
              </ul>
            </div>
          )}

          {app.knownStruggles?.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-slate-700 mb-1">Things you're working through</h3>
              <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
                {app.knownStruggles.map((s, i) => <li key={i}>{s.topic || s}</li>)}
              </ul>
            </div>
          )}

          {app.relationshipMap && Object.keys(app.relationshipMap).length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-slate-700 mb-1">People you've mentioned</h3>
              <ul className="text-sm text-slate-700 space-y-1">
                {Object.entries(app.relationshipMap).map(([role, data]) => (
                  <li key={role}>
                    <span className="font-medium">{role}:</span> {data?.name || 'unnamed'}
                    {data?.notes && <span className="text-slate-500"> — {data.notes}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {app.patternsNoticed?.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-slate-700 mb-1">Patterns noticed</h3>
              <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
                {app.patternsNoticed.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
          )}

          {!app.activeGoals?.length && !app.knownStruggles?.length && !Object.keys(app.relationshipMap || {}).length && !app.patternsNoticed?.length && (
            <p className="text-sm text-slate-500 italic">Nothing remembered yet — it builds up as you chat.</p>
          )}
        </section>
      ))}

      {/* Actions */}
      <section className="bg-white rounded-xl shadow-sm p-5 mb-5">
        <h2 className="font-semibold text-slate-900 mb-3">Export or delete</h2>
        <button
          onClick={() => exportData(false)}
          className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium text-slate-800"
        >
          <Download size={16} /> Download my conversations
        </button>
        <button
          onClick={() => exportData(true)}
          className="block mt-2 mb-3 text-xs text-slate-500 hover:text-slate-700 underline"
        >
          Or download the full technical file (JSON)
        </button>

        {!confirmDelete ? (
          <button
            onClick={() => setConfirmDelete(true)}
            className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-700 hover:bg-red-50 rounded-lg text-sm font-medium"
          >
            <Trash2 size={16} /> Delete all coaching data
          </button>
        ) : (
          <div className="border border-red-300 bg-red-50 rounded-lg p-4">
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle className="text-red-600 mt-0.5 flex-shrink-0" size={18} />
              <p className="text-sm text-red-900">
                This permanently deletes your coaching profile and everything {COACH_NAME} remembers. Can't be undone.
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={deleteAll} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
                Yes, delete everything
              </button>
              <button onClick={() => setConfirmDelete(false)} className="px-4 py-2 text-slate-600 hover:text-slate-900 text-sm">
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>

      <p className="text-xs text-slate-500 italic">
        {saving && 'Saving…'}
      </p>
    </div>
  );
}
